import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import AnimatedCounter from "./AnimatedCounter";
import ExportBtn from "./ExportBtn";
import TablePaginationFooter from "./TablePaginationFooter";
import { getDashboardVariants } from "./dashboardAnimations";
import styles from "./css/transaction.module.css";
import { renderDefaultRow } from "./TableRender";

const TransactionLayout = ({
  title,
  subtitle,
  stats = [],
  searchQuery = "",
  onSearchChange,
  searchPlaceholder = "Search transactions...",
  onExport,
  cardTitle = "Recent Ledger",
  cardSubtitle = "Real-time payment gateway ledger",
  columns = [],
  data = [],
  renderRow,
  isLoading = false,
  emptyMessage = "No transactions found.",
  pagination = null,
}) => {
  const [copiedText, setCopiedText] = useState(null);
  const shouldReduceMotion = useReducedMotion();

  const {
    headerContainerVariants: containerVariants,
    textFadeUpVariants: springFloatVariants,
    tableBodyVariants,
    tableRowVariants,
  } = getDashboardVariants(shouldReduceMotion);

  const levitateHover = shouldReduceMotion
    ? {}
    : {
      whileHover: {
        y: -3,
        boxShadow: "0 0 20px rgba(111, 230, 184, 0.06)",
      },
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    };

  const { currentPage = 1, totalCount = 0, limit = 10, onPageChange, onLimitChange } = pagination || {};
  const totalPages = Math.ceil(totalCount / limit) || 1;

  return (
    <div className={styles.pageContainer}>
      <motion.div
        className={styles.contentWrapper}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Page Header */}
        {(title || subtitle) && (
          <motion.div variants={springFloatVariants} className={styles.pageHeader}>
            {title && <h1 className={styles.pageTitle}>{title}</h1>}
            {subtitle && <p className={styles.pageSubtitle}>{subtitle}</p>}
          </motion.div>
        )}

        {/* Stats Row with Levitation & Counter Numbers */}
        {stats.length > 0 && (
          <motion.div variants={springFloatVariants} className={styles.statsRow}>
            {stats.map((stat, idx) => (
              <motion.div key={idx} className={styles.statCard} {...levitateHover}>
                {stat.icon && (
                  <div className={styles.statIcon}>
                    <i className={stat.icon}></i>
                  </div>
                )}
                <div className={styles.statTextGroup}>
                  <span className={styles.statLabel}>{stat.label}:</span>
                  <span className={styles.statValue}>
                    <AnimatedCounter value={stat.value} />
                  </span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Search Toolbar */}
        {(onSearchChange || onExport || data.length > 0) && (
          <motion.div variants={springFloatVariants} className={styles.toolbar}>
            {onSearchChange && (
              <div className={styles.searchBox}>
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                />
              </div>
            )}

            {/* Export Dropdown Component */}
            <ExportBtn
              onExport={onExport}
              columns={columns}
              data={data}
              title={title}
            />
          </motion.div>
        )}

        {/* Table Card Frame */}
        <motion.div variants={springFloatVariants} className={styles.tableCard} {...levitateHover}>
          {(cardTitle || cardSubtitle) && (
            <div className={styles.tableCardHeader}>
              <div>
                {cardTitle && <h5 className={styles.tableCardTitle}>{cardTitle}</h5>}
                {cardSubtitle && <p className={styles.tableCardSubtitle}>{cardSubtitle}</p>}
              </div>
            </div>
          )}

          <div className={styles.tableResponsiveFix}>
            <table className={styles.customTable}>
              <thead>
                <tr>
                  {columns.map((col, idx) => (
                    <th key={col.key || idx} className={col.className} style={col.style}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>

              {isLoading ? (
                <tbody>
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={idx}>
                      {columns.map((col, cIdx) => (
                        <td key={cIdx} className={col.className}>
                          <span
                            className={styles.skeletonBox}
                            style={{ width: cIdx === 0 ? "30px" : cIdx === columns.length - 1 ? "110px" : "80px" }}
                          ></span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              ) : data.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={columns.length || 1} className={styles.noTrxTd}>
                      <div className={styles.emptyIconWrapper}>
                        <i className="fas fa-inbox"></i>
                      </div>
                      <div>{emptyMessage}</div>
                    </td>
                  </tr>
                </tbody>
              ) : (
                <motion.tbody
                  variants={tableBodyVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {data.map((item, index) => {
                    const globalRowIndex = index + (currentPage - 1) * limit + 1;
                    if (renderRow) {
                      return renderRow(item, index, globalRowIndex, tableRowVariants, levitateHover);
                    }
                    return renderDefaultRow(item, index, globalRowIndex, columns, {
                      tableRowVariants,
                      levitateHover,
                      styles,
                      copiedText,
                      setCopiedText,
                    });
                  })}
                </motion.tbody>
              )}
            </table>
          </div>

          {/* Pagination Footer */}
          {pagination && (
            <TablePaginationFooter
              currentPage={currentPage}
              totalCount={totalCount}
              limit={limit}
              currentCount={data.length}
              onPageChange={onPageChange}
              onLimitChange={onLimitChange}
            />
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default TransactionLayout;
